<template>
  <v-menu offset-y location="bottom right">
    <template #activator="{ props }">
      <v-btn rounded color="primary" v-bind="props" size="large" prepend-icon="$add">
        {{ $t("reports.exports.newExport") }}
      </v-btn>
    </template>
    <v-list>
      <v-list-item
        v-for="action in exportActions"
        :key="action"
        @click="handleExportAction(action)"
      >
        <v-list-item-title>
          {{ $t(`mspReports.exports.actions.${action}`) }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script lang="ts">
import { type Component, computed, defineComponent } from "vue";
import { useDialogsStore } from "@/_store/dialogs.module";
import { ModalWidth } from "@/constants/modals";
import { isMspActionRestricted } from "@/_helpers/msp-permissions.ts";
import { MspExportAction } from "@/constants/msp-exports.ts";
import MspExportTicketsModal from "@/components/modals/msp/MspExportTicketsModal.vue";
import MspExportUsersModal from "@/components/modals/msp/MspExportUsersModal.vue";
import MspExportDevicesModal from "@/components/modals/msp/MspExportDevicesModal.vue";
import MspExportWorkspacesModal from "@/components/modals/msp/MspExportWorkspacesModal.vue";
import { useMspExportsStore } from "@/_store/msp/msp-exports.module.ts";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import { useI18n } from "vue-i18n";
import { componentDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";
import { useMspBillingUsageExport } from "@/composables/useMspBillingUsageExport.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import type { ExportDialogPayload } from "@/_store/exports.module.ts";
import type {
  GlobalWorkspacesFilters,
  MspDevicesFilters,
  MspFilters,
  MspTicketsFilters,
  MspUsersFilters,
} from "@/_store/filters.module.ts";
import { MspPortalScopeSection, MspRolePermissionScopes } from "@/constants/roles.ts";

// Each action maps to the filter shape its exportFn expects.
// This drives the per-entry typing of `actionConfigs` below — each entry's `exportFn`
// is automatically typed for the right `ExportDialogPayload<TFilters>`.
type ActionFiltersMap = {
  [MspExportAction.EXPORT_TICKETS]: MspTicketsFilters;
  [MspExportAction.EXPORT_TICKETS_SUMMARY]: MspTicketsFilters;
  [MspExportAction.EXPORT_USERS]: MspUsersFilters;
  [MspExportAction.EXPORT_DEVICES]: MspDevicesFilters;
  [MspExportAction.EXPORT_ADMIN_NOTIFICATIONS]: MspFilters | GlobalWorkspacesFilters;
  [MspExportAction.EXPORT_WORKSPACES_SUMMARY]: MspFilters | GlobalWorkspacesFilters;
  [MspExportAction.EXPORT_WORKSPACES_LIST]: MspFilters | GlobalWorkspacesFilters;
};

type ActionConfig<A extends keyof ActionFiltersMap> = {
  exportFn: (data: ExportDialogPayload<ActionFiltersMap[A]>) => Promise<void>;
  component: Component;
  dialogProps?: {
    disablePersistentAnimation?: boolean;
  };
};

type ActionConfigMap = { [A in keyof ActionFiltersMap]?: ActionConfig<A> };

export default defineComponent({
  setup() {
    const i18n = useI18n();
    const dialogsStore = useDialogsStore();
    const mspExportsStore = useMspExportsStore();
    const workspaceStore = useWorkspaceStore();
    const { openBillingUsageExport } = useMspBillingUsageExport();

    const exportActions = computed<MspExportAction[]>(() => {
      return [
        MspExportAction.EXPORT_TICKETS,
        MspExportAction.EXPORT_TICKETS_SUMMARY,
        MspExportAction.EXPORT_DEVICES,
        MspExportAction.EXPORT_USERS,
        MspExportAction.EXPORT_WORKSPACES_LIST,
        MspExportAction.EXPORT_WORKSPACES_SUMMARY,
        MspExportAction.EXPORT_ADMIN_NOTIFICATIONS,
        MspExportAction.EXPORT_BILLING_USAGE,
      ];
    });

    const actionConfigs: ActionConfigMap = {
      [MspExportAction.EXPORT_TICKETS]: {
        exportFn: mspExportsStore.exportTicketsToCsv,
        component: MspExportTicketsModal,
        dialogProps: { disablePersistentAnimation: true },
      },
      [MspExportAction.EXPORT_TICKETS_SUMMARY]: {
        exportFn: mspExportsStore.exportTicketsSummaryToCsv,
        component: MspExportTicketsModal,
        dialogProps: { disablePersistentAnimation: true },
      },
      [MspExportAction.EXPORT_USERS]: {
        exportFn: mspExportsStore.exportUsersToCsv,
        component: MspExportUsersModal,
      },
      [MspExportAction.EXPORT_DEVICES]: {
        exportFn: mspExportsStore.exportDevicesToCsv,
        component: MspExportDevicesModal,
      },
      [MspExportAction.EXPORT_ADMIN_NOTIFICATIONS]: {
        exportFn: mspExportsStore.exportAdminNotifications,
        component: MspExportWorkspacesModal,
      },
      [MspExportAction.EXPORT_WORKSPACES_SUMMARY]: {
        exportFn: mspExportsStore.exportWorkspacesSummary,
        component: MspExportWorkspacesModal,
      },
      [MspExportAction.EXPORT_WORKSPACES_LIST]: {
        exportFn: mspExportsStore.exportWorkspacesList,
        component: MspExportWorkspacesModal,
      },
    };

    const handleExportAction = (action: MspExportAction) => {
      if (action === MspExportAction.EXPORT_BILLING_USAGE) {
        openBillingUsageExport({ workspaceId: workspaceStore.workspace.workspaceId });
        return;
      }

      // `MspExportAction` enum has values that don't appear in `ActionFiltersMap`
      // (e.g. `EXPORT_USERS_AND_DEVICES_TREND` has no exportFn). The cast narrows
      // the lookup; unconfigured actions return `undefined` and short-circuit below.
      const config = actionConfigs[action as keyof ActionConfigMap];
      if (!config) return; // Exit if the action is not configured.

      const disable = isMspActionRestricted(
        MspRolePermissionScopes.MSP_PORTAL_SCOPE,
        MspPortalScopeSection.MSP_REPORTS
      );

      dialogsStore.openDialog({
        ...componentDialogsConfigConstructor({
          action,
          callback: async (data) => {
            await config.exportFn(data);
            useSnackbarStore().addGenericSuccess(
              i18n.t("snackbar.messages.general.exportCsvStarted", {
                pageName: i18n.t("reports.title"),
              })
            );
          },
          component: config.component,
          width: ModalWidth.LARGE,
          disable,
        }),
        ...config.dialogProps,
      });
    };
    return {
      exportActions,
      handleExportAction,
    };
  },
});
</script>
